import { FC, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Edit, X } from 'lucide-react';
import { trpc } from 'src/trpc';
import { useToast } from 'components/useToast';
import { Cleanup } from 'src/trpc';
import { Label } from '@/components/ui/label';

interface EditCleanupModalProps {
  cleanup: Cleanup;
  trigger?: React.ReactNode;
}

export const EditCleanupModal: FC<EditCleanupModalProps> = ({ cleanup, trigger }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [descriptionItems, setDescriptionItems] = useState<string[]>(['']);
  const [isTemplate, setIsTemplate] = useState(false);
  const [position, setPosition] = useState<number>(0);
  const { successToast, errorToast } = useToast();

  const utils = trpc.useContext();

  // Initialize form with cleanup data
  useEffect(() => {
    if (cleanup) {
      setTitle(cleanup.title);
      setDescriptionItems(cleanup.descriptionItems);
      setIsTemplate(cleanup.isTemplate || false);
      setPosition(cleanup.position || 0);
    }
  }, [cleanup]);

  const updateCleanupMutation = trpc.admin.cleanup.updateCleanup.useMutation({
    onSuccess: () => {
      successToast({ title: 'Cleanup updated successfully' });
      setOpen(false);
      utils.admin.cleanup.getCleanups.invalidate();
    },
    onError: (error) => {
      errorToast({ title: 'Failed to update cleanup', description: error.message });
    },
  });

  const addDescriptionItem = () => {
    setDescriptionItems([...descriptionItems, '']);
  };

  const removeDescriptionItem = (index: number) => {
    if (descriptionItems.length > 1) {
      setDescriptionItems(descriptionItems.filter((_, i) => i !== index));
    }
  };

  const updateDescriptionItem = (index: number, value: string) => {
    const newItems = [...descriptionItems];
    newItems[index] = value;
    setDescriptionItems(newItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      errorToast({ title: 'Title is required', description: 'Please provide a title for the cleanup.' });
      return;
    }

    if (descriptionItems.filter((item) => item.trim()).length === 0) {
      errorToast({ title: 'At least one description item is required', description: 'Please add at least one description item.' });
      return;
    }

    const updateData = {
      title: title.trim(),
      descriptionItems: descriptionItems.filter((item) => item.trim()),
      isTemplate,
      position,
    };

    try {
      await updateCleanupMutation.mutateAsync({
        id: cleanup._id.toString(),
        updates: updateData,
      });
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const isValid = title.trim() && descriptionItems.filter((item) => item.trim()).length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-gray-900">
              Edit Cleanup Service
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Cleanup Service Title</Label>
                  <Input
                    id="title"
                    placeholder="Cleanup Service Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div>
                  <div className="space-y-2">
                    {descriptionItems.map((item, index: number) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Label htmlFor="title" style={{ minWidth: 60 }}>
                          Item {index + 1}
                        </Label>
                        <Input
                          value={item}
                          placeholder={`Item ${index + 1} Description`}
                          onChange={(e) => updateDescriptionItem(index, e.target.value)}
                          required
                        />
                        {descriptionItems.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeDescriptionItem(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addDescriptionItem}
                      className="w-full"
                    >
                      Add Description Item
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="position">Position</Label>
                    <Input
                      id="position"
                      type="number"
                      placeholder="Position"
                      value={position || ''}
                      onChange={(e) => setPosition(Number(e.target.value))}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      id="isTemplate"
                      type="checkbox"
                      checked={isTemplate}
                      onChange={(e) => setIsTemplate(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="isTemplate" className="text-sm font-medium text-gray-700">
                      Template
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isValid || updateCleanupMutation.isPending}>
                  {updateCleanupMutation.isPending ? 'Updating...' : 'Update Cleanup'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
